import { apiRequest } from "./api.client";

// ─── Types ─────────────────────────────────────────────────────────────────────
// Mirrors kaagazy-backend/app/modules/queue/schemas.py

export type QueuePriorityTag = "senior_citizen" | "emergency" | "staff_family" | "none";

export type QueueState = "waiting" | "in_progress" | "seen" | "left" | "deferred" | "rejected";

export interface QueueEntry {
  queue_entry_id: string;
  clinic_id: string;
  doctor_id: string;
  patient_id: string;
  appointment_id: string | null;
  queue_date: string;
  position: number;
  priority_tag: QueuePriorityTag;
  state: QueueState;
  joined_at: string;
  started_at: string | null;
  seen_at: string | null;
  left_at: string | null;
  suggested_time: string | null;
  eta_minutes: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface QueueEntryListItem {
  queue_entry_id: string;
  doctor_id: string;
  doctor_name: string;
  patient_id: string;
  patient_name: string;
  patient_phone: string | null;
  appointment_id: string | null;
  queue_date: string;
  position: number;
  priority_tag: QueuePriorityTag;
  state: QueueState;
  joined_at: string;
  started_at: string | null;
  seen_at: string | null;
  left_at: string | null;
  suggested_time: string | null;
  eta_minutes: number | null;
}

export interface WalkinRegisterResponse {
  entry: QueueEntry;
  beyond_hours_warning: boolean;
}

export interface WalkinRegisterPayload {
  patient_id: string;
  doctor_id: string;
  priority_tag: QueuePriorityTag;
}

export interface PublicQueueStatus {
  position: number;
  eta_minutes: number | null;
  state: QueueState;
  queue_date: string;
}

// Unauthenticated - the token in the path is the credential, not a Cognito
// session. Kept separate from QueueAPI since its auth story is different.
export const getPublicQueueStatus = (token: string): Promise<PublicQueueStatus> =>
  apiRequest<PublicQueueStatus>(`/public/queue/${token}`);

export const QueueAPI = {
  register: (payload: WalkinRegisterPayload): Promise<WalkinRegisterResponse> =>
    apiRequest<WalkinRegisterResponse>("/queue/register", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  list: (params: { doctor_id: string; queue_date?: string; states?: string[] }): Promise<QueueEntryListItem[]> => {
    const qs = new URLSearchParams();
    qs.set("doctor_id", params.doctor_id);
    if (params.queue_date) qs.set("queue_date", params.queue_date);
    params.states?.forEach((s) => qs.append("states", s));
    return apiRequest<QueueEntryListItem[]>(`/queue?${qs.toString()}`);
  },

  reorder: (payload: {
    doctor_id: string;
    queue_date: string;
    ordered_queue_entry_ids: string[];
    reason?: string;
  }): Promise<QueueEntry[]> =>
    apiRequest<QueueEntry[]>("/queue/reorder", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  accept: (id: string): Promise<QueueEntry> =>
    apiRequest<QueueEntry>(`/queue/${id}/accept`, { method: "POST" }),

  defer: (id: string, reason?: string): Promise<QueueEntry> =>
    apiRequest<QueueEntry>(`/queue/${id}/defer`, { method: "POST", body: JSON.stringify({ reason }) }),

  reject: (id: string, reason?: string): Promise<QueueEntry> =>
    apiRequest<QueueEntry>(`/queue/${id}/reject`, { method: "POST", body: JSON.stringify({ reason }) }),

  markSeen: (id: string): Promise<QueueEntry> =>
    apiRequest<QueueEntry>(`/queue/${id}/seen`, { method: "POST" }),

  markLeft: (id: string, reason?: string): Promise<QueueEntry> =>
    apiRequest<QueueEntry>(`/queue/${id}/left`, { method: "POST", body: JSON.stringify({ reason }) }),
};
